import React, { FC } from "react";
import LmsTable from "../ui/LmsTable";
import { useParseRouteActions, useFetchData, removeProp, appendParams } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { GlobalConstants } from "../../constants/GlobalConstants";

const LmsServicingModifyBudgetsMainPage: FC = () => {
  return (
    <LmsTable
      apiSuffix="budget/properties"
      actionButtons={["edit", "export"]}
      columnLabelsDictionary={{
        applicationNumber: "Application #",
        borrowerOrganization: "Borrower",
      }}
      columnClickOptions={{
        loan: {
          idProp: "id",
          path: "/servicing-modify-budgets/edit",
        }
      }}
    />
  );
};

const AddVendorForm = ({ setIsModalOpen, options }) => {
  const handleSuccess = (response) => {
    const payeeOrganizationIds = options?.useFormInstance?.inputs?.payeeOrganizationIds || [];
    options?.handleFormChange(null, { name: "payeeOrganizationIds", value: [...payeeOrganizationIds, response?.vendroRequestId] }, true);
  };

  const myForm = new BCFormController({}, {
    values: {
      loanBudgetId: options?.useFormInstance?.inputs?.loanBudgetId
    },
    submitSuffix: "budget/vendor-request",
    onSubmitSuccess: handleSuccess
  });

  myForm.addCenteredGroup([myForm.getInputField("Vendor Name", { name: "name", required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Email", { validations: ["required", "email"] })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Message", { name: "description", required: true })]);

  const formActions = [{ action: "save-close", text: "Submit" }, "close"];

  return <LmsFormGenerator myForm={myForm} actions={formActions} handleClose={() => setIsModalOpen(false)}/>;
};

const AddBudgetDetailsLineItemForm = (
  {
    setIsAddBudgetRowOpen,
    loanBudgetId,
    loanPropertyId,
    sessionId,
    setLoanPropertyDetails,
    loanPropertyDetails,
    modifyBudgetOptions
  }) => {
  const budgetDetailsSuffix = appendParams(`loanproperty/budget-details/${loanBudgetId}/initial-data`, [{ loanPropertyId, sessionId }]);
  const [{ data, isLoading, error }] = useFetchData(budgetDetailsSuffix);
  const initialValues = removeProp(data, "loanBudgetCategoryId");

  const onSubmitSuccess = (responseData, options) => {
    if (responseData?.id && modifyBudgetOptions?.inputs) {
      const newLoanPropertyDetails = {
        ...loanPropertyDetails,
        budgetTempDetails: loanPropertyDetails.budgetTempDetails?.map(budget => {
          if (modifyBudgetOptions?.inputs?.[budget?.id]) {
            return { ...budget, ...modifyBudgetOptions?.inputs?.[budget?.id] };
          }
          else {
            return budget;
          }
        })
      };

      const budgetCategoryName = options?.allDropdownOptionsData?.["budget/categories"]?.find((item) => item.id === responseData?.loanBudgetCategoryId)?.name;
      newLoanPropertyDetails.budgetTempDetails.push({
        ...responseData,
        display: budgetCategoryName,
        percentWorkComplete: 0,
        budgetAmount: 0,
        disbursedAmount: 0,
        isClosed: false
      });

      setLoanPropertyDetails(newLoanPropertyDetails);
    }
  };

  const myForm = new BCFormController(initialValues, {
    values: { loanBudgetId, loanPropertyId },
    submitSuffix: "draws/modify-budget-temp",
    displayMessageLocally: true,
    submitMethod: "POST",
    excludeFromSubmit: ["id"],
    onSubmitSuccess,
    blankWidth: 4,
  });

  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Borrower", { name: "borrowerOrganizationDisplay" })]);
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Special Purpose Entity", { name: "spe" })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Application #", { name: "applicationNumberDisplay" })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Loan #", { name: "loanNumberDisplay" })]);
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Loan Property", { name: "loanPropertyDisplay", required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Budget Category", "budget/categories", { required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Loan Budget Sub Category")]);
  myForm.addCenteredGroup([myForm.getNumberField("Month Of Remaining Expenditure")]);
  myForm.addCenteredGroup([myForm.getDropdownField("Payee", "draws/payee-organizations", {
    actionLabel: "Add Vendor",
    modalContent: (setIsAddFormModalOpen, options) => <AddVendorForm setIsModalOpen={setIsAddFormModalOpen} options={options}/>,
    actionIcon: GlobalConstants.Icons.CARET_UP,
    name: "payeeOrganizationIds",
    dropdownMultiple: true
  })]);

  return <LmsFormGenerator
    myForm={myForm}
    isLoading={isLoading}
    error={error}
    actions={["save-close", "close"]}
    handleClose={() => setIsAddBudgetRowOpen(false)}
  />;
};

export const LmsModifyBudget = ({ id, handleClose = undefined as any }) => {
  const [{ data: loanPropertyDetails = {}, setData: setLoanPropertyDetails, isLoading }] = useFetchData(`loanproperty/budget-details/${id}`);
  const budgetDetailsRows = loanPropertyDetails?.budgetTempDetails || [];
  const loanBudgetId = budgetDetailsRows[0] ? budgetDetailsRows[0]?.loanBudgetId : "";
  const sessionId = loanPropertyDetails?.sessionId;
  const loanPhaseDisplay = loanPropertyDetails?.loanPhaseDisplay || "";
  const isProductionLoanPhase = loanPhaseDisplay === "Production";
  const isServicingLoanPhase = loanPhaseDisplay === "Servicing";

  const dataFormatterFunc = (inputsData) => {
    return {
      loanPropertyId: id,
      sessionId: sessionId,
      budgetTempDetails: budgetDetailsRows?.map(({ id }) => {
        return { id, isClosed: !!inputsData[id]?.isClosed, budgetAmount: parseFloat(inputsData[id]?.budgetAmount) };
      })
    };
  };

  const myForm = new BCFormController({ ...loanPropertyDetails, isProductionLoanPhase }, {
    submitSuffix: "loanproperty/budget-details",
    submitMethod: "PUT",
    dataFormatterFunc,
    errorTitle: isServicingLoanPhase ? "You cannot modify budget in Servicing phase. Please create Amendment." : undefined,
    isCompact: true,
    initIsDisabled: !isProductionLoanPhase,
  });

  myForm.addGroupFields([
    myForm.getDisplayField("Borrower", { name: "loanBorrowerOrganization" }),
    myForm.getDisplayField("Loan", { name: "loanDisplay" }),
    myForm.getCurrencyField("Total Budget", { name: "budgetAmountTotal", disabled: true }),
    myForm.getCurrencyField("Funds To Allocate", {
      name: "fundsToAllocate",
      value: 0,
      disabled: true
    }),
    myForm.getDisplayField("Property", { name: "display" }),
  ]);
  myForm.addDividerField({ dividerIsHidden: true });
  myForm.addGroupFields([
    myForm.getButtonField("Add", undefined, {
      width: 1,
      buttonIcon: GlobalConstants.Icons.CARET_UP,
      modalContent: (setIsAddBudgetRowOpen, modifyBudgetOptions) =>
          isProductionLoanPhase && budgetDetailsRows[0].display !== "Placeholder" ? (
            <AddBudgetDetailsLineItemForm
              setIsAddBudgetRowOpen={setIsAddBudgetRowOpen}
              loanBudgetId={loanBudgetId}
              loanPropertyId={id}
              sessionId={sessionId}
              loanPropertyDetails={loanPropertyDetails}
              setLoanPropertyDetails={setLoanPropertyDetails}
              modifyBudgetOptions={modifyBudgetOptions}
            />
          ) : (
            <>You cannot add new line items in a Placeholder budget.</>
          ),
    }),
    myForm.getBlankField({ width: 12 }),
  ], { widths: 1, isHiddenFunc: () =>  !isProductionLoanPhase });
    myForm.addGroupFields([
    myForm.getLabelField("Category", { position: "center", width: 3 }),
    myForm.getLabelField("Sub-category", { position: "center", width: 2 }),
    myForm.getLabelField("Budget", { position: "center", width: 2, required: true }),
    myForm.getLabelField("Disbursed", { position: "center", width: 2 }),
    myForm.getLabelField("Budget % Complete", { position: "center", width: 2 }),
    myForm.getLabelField("Work % Complete", { position: "center", width: 2 }),
    myForm.getLabelField("Available", { position: "center", width: 2 }),
    myForm.getLabelField("Line Closed?", { position: "center", width: 1 }),
  ], { widths: 1 });

  if (budgetDetailsRows) {
    budgetDetailsRows?.map((
      {
        id,
        display,
        loanBudgetSubCategory,
        percentWorkComplete = 0,
        budgetAmount = 0,
        disbursedAmount = 0,
        isClosed
      }) => {
      const budgetComplete = !isNaN(disbursedAmount / budgetAmount * 100) ? disbursedAmount / budgetAmount * 100 : 0;
      const workComplete = percentWorkComplete ? percentWorkComplete * 100 : 0;
      const available = budgetAmount - disbursedAmount;
      myForm.addGroupFields([
        myForm.getLabelField(display || "", { width: 3, wrapLabel: true }),
        myForm.getLabelField(loanBudgetSubCategory || "", { width: 2, wrapLabel: true, fontWeightLight: true }),
        myForm.getCurrencyField("Budget", { label: "", name: "budgetAmount", value: budgetAmount, nestedInputsProp: id, calculatedTargetField: "budgetTotal", width: 2 }),
        myForm.getCurrencyField("Disbursed", { label: "", name: "disbursedAmount", value: disbursedAmount, nestedInputsProp: id, disabled: true, width: 2 }),
        myForm.getInputField("Budget % Complete", {
          label: "",
          fieldType: "percent",
          name: "budgetComplete",
          value: budgetComplete,
          nestedInputsProp: id,
          disabled: true,
          width: 2
        }),
        myForm.getInputField("Work % Complete", {
          label: "",
          name: "percentWorkComplete",
          value: workComplete,
          fieldType: "percent",
          nestedInputsProp: id,
          disabled: true,
          width: 2
        }),
        myForm.getCurrencyField("Available", { label: "", value: available, nestedInputsProp: id, disabled: true, width: 2 }),
        myForm.getCheckboxField("Line Closed", { label: "", name: "isClosed", value: isClosed, nestedInputsProp: id, width: 1 }),
      ], { widths: 1 });
    });
  }

  myForm.addGroupFields([
    myForm.getBlankField({ width: 3 }),
    myForm.getLabelField("Totals", { width: 2, position: "center" }),
    myForm.getCurrencyField("budgetTotal", { label: "", width: 2, disabled: true }),
    myForm.getCurrencyField("disbursedTotal", { label: "", width: 2, disabled: true }),
    myForm.getInputField("budgetPercentCompleteTotal", { label: "", width: 2, fieldType: "percent", disabled: true }),
    myForm.getBlankField({ width: 2 }),
    myForm.getCurrencyField("availableTotal", { label: "", width: 2, disabled: true }),
    myForm.getBlankField({ width: 1 }),
  ], { widths: 1 });

  const handleSubmit = (inputs, options) => {
    if (inputs.fundsToAllocate !== 0) {
      options.setFormErrorMessage({ message: "All funds have not been reconciled" });
    } else {
      options.handleSubmit("loanproperty/budget-details", { 
        method: "PUT", 
        data: dataFormatterFunc(inputs), 
        buttonText: options.buttonText,
        onSuccessFunc: options.onSuccessFunc,
      });
    }
  };

  const onFormChanged = ({ inputs, setInputs }, lastUpdated) => {
    if (lastUpdated === "budgetAmount") {
      const initTotal = loanPropertyDetails.budgetAmountTotal || 0;
      setInputs({ ...inputs, fundsToAllocate: initTotal - inputs.budgetTotal });
    }
  };

  return <>
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      handleSubmit={handleSubmit}
      actions={isProductionLoanPhase ? ["save", "save-close", "close"] : ["close"]}
      handleClose={handleClose}
      onFormChanged={onFormChanged}
    />
  </>;
};

const LmsServicingModifyBudgets = ({ params = {}, history }) => {
  const { isMainPage, isEdit, isActionAllowed, id } = useParseRouteActions(params, ["edit"]);

  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <LmsServicingModifyBudgetsMainPage />}
      {isEdit && <LmsModifyBudget id={id} />}
    </>
  );
};

export default LmsServicingModifyBudgets;